/**
 * How the probe and optimizer read a model.
 *
 * gltf-transform refuses a file whose required extension has no registered
 * handler, and drops unregistered ones on write. So every Khronos extension it
 * supports is registered (spec-gloss from Sketchfab, specular, transmission,
 * ior, ...) plus `TRACE_animation_binding` for avatar and performance GLBs.
 *
 * Draco needs a decoder, loaded by Babylon's Draco loader: in the browser from
 * `DracoDecoder.DefaultConfiguration` (the Babylon CDN unless a host overrides
 * it), the files the viewer renders Draco with; in Node from `draco3dgltf`, an
 * optional peer. Meshopt's decoder ships in `meshoptimizer`. Each loads only
 * when a file declares its extension (gltf-transform would install a missing
 * decoder as `undefined` and throw), and both extensions are dropped after
 * reading, so output is written uncompressed: neither is read by every Trace
 * runtime (see `extensions.ts`).
 */

import type { Document } from '@gltf-transform/core';
import { NodeIO } from '@gltf-transform/core';
import { ALL_EXTENSIONS, EXTMeshoptCompression, KHRDracoMeshCompression } from '@gltf-transform/extensions';
import { TraceAnimationBinding } from './traceAnimationBinding';

const EXTENSIONS = [...ALL_EXTENSIONS, TraceAnimationBinding];

/** Compression extensions, each with the decoder gltf-transform needs to read it. */
const COMPRESSION = [
  { name: KHRDracoMeshCompression.EXTENSION_NAME, label: 'Draco', key: 'draco3d.decoder', load: loadDracoDecoder },
  { name: EXTMeshoptCompression.EXTENSION_NAME, label: 'meshopt', key: 'meshopt.decoder', load: loadMeshoptDecoder },
];

let dracoDecoder: Promise<unknown> | undefined;

/**
 * In Node, `draco3dgltf` and its wasm bytes, handed to Babylon as `jsModule` +
 * `wasmBinary` (under a DOM shim such as happy-dom, emscripten would otherwise
 * try to fetch the wasm). In the browser, nothing: Babylon loads its configured
 * script and wasm.
 */
async function nodeDecoderConfig(): Promise<{ jsModule?: unknown; wasmBinary?: ArrayBuffer }> {
  // Read off globalThis: browser builds of this module have no Node types.
  if (!(globalThis as { process?: { versions?: { node?: string } } }).process?.versions?.node) return {};
  // Variable specifiers keep bundlers from pulling Node modules into a browser bundle.
  const [draco, nodeModule, fs] = ['draco3dgltf', 'node:module', 'node:fs/promises'];
  const [draco3d, { createRequire }, { readFile }] = (await Promise.all([
    import(/* @vite-ignore */ draco),
    import(/* @vite-ignore */ nodeModule),
    import(/* @vite-ignore */ fs),
  ])) as [
    { default: { createDecoderModule: unknown } },
    { createRequire(url: string): { resolve(id: string): string } },
    { readFile(path: string): Promise<Uint8Array> },
  ];
  const bytes = await readFile(createRequire(import.meta.url).resolve('draco3dgltf/draco_decoder_gltf.wasm'));
  const wasmBinary = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  return { jsModule: draco3d.default.createDecoderModule, wasmBinary };
}

/**
 * The raw Draco decoder module gltf-transform needs, loaded once per page or
 * process through Babylon's own Draco loader. Babylon's public API decodes only
 * to Babylon vertex data (in workers by default); with no workers it keeps the
 * module on the protected `_modulePromise`, which a subclass may read.
 */
function loadDracoDecoder(): Promise<unknown> {
  dracoDecoder ??= (async () => {
    const [{ DracoDecoder }, nodeConfig] = await Promise.all([
      import('@babylonjs/core/Meshes/Compression/dracoDecoder'),
      nodeDecoderConfig(),
    ]);
    class DracoModuleLoader extends DracoDecoder {
      get module(): Promise<{ module: unknown }> | undefined {
        return this._modulePromise;
      }
    }
    const loader = new DracoModuleLoader({ ...DracoDecoder.DefaultConfiguration, numWorkers: 0, ...nodeConfig });
    const loaded = await loader.module;
    if (!loaded) throw new Error('no Draco decoder is configured');
    return loaded.module;
  })();
  // A failed load (offline, blocked CDN, peer not installed) is retried on the next file.
  dracoDecoder.catch(() => {
    dracoDecoder = undefined;
  });
  return dracoDecoder;
}

async function loadMeshoptDecoder(): Promise<unknown> {
  const { MeshoptDecoder } = await import('meshoptimizer');
  await MeshoptDecoder.ready;
  return MeshoptDecoder;
}

/**
 * Read `extensionsUsed` / `extensionsRequired` straight from a GLB/glTF's JSON,
 * verbatim.
 *
 * gltf-transform's `root.listExtensionsUsed()` only returns extensions it has a
 * registered plugin for — so an unregistered (or vendor) extension is dropped,
 * and the list reads empty even when the file declares several. The raw JSON
 * always lists them, which is what a creator needs to see. Returns empty lists
 * if the bytes can't be parsed as GLB/glTF (the caller still has
 * gltf-transform's view as a fallback).
 *
 * Reading the JSON header is also independent of decoding the buffers, so this
 * still works for a file the parser rejects outright (e.g. an unknown required
 * extension) — which is exactly when the creator most needs to be told which
 * extension is to blame.
 */
export function readDeclaredExtensions(buffer: ArrayBuffer): { used: string[]; required: string[] } {
  try {
    const view = new DataView(buffer);
    let jsonText: string;
    // GLB starts with magic 0x46546C67 ("glTF"); otherwise treat as plain JSON.
    if (buffer.byteLength >= 12 && view.getUint32(0, true) === 0x46546c67) {
      // First chunk (type 0x4E4F534A "JSON") holds the document.
      const chunkLength = view.getUint32(12, true);
      const jsonBytes = new Uint8Array(buffer, 20, chunkLength);
      jsonText = new TextDecoder().decode(jsonBytes);
    } else {
      jsonText = new TextDecoder().decode(buffer);
    }
    const gltf = JSON.parse(jsonText) as { extensionsUsed?: unknown; extensionsRequired?: unknown };
    const strings = (value: unknown): string[] =>
      Array.isArray(value) ? value.filter((e): e is string => typeof e === 'string') : [];
    const used = strings(gltf.extensionsUsed);
    const required = strings(gltf.extensionsRequired);
    // Some exporters list a required extension only in `extensionsRequired`.
    // The spec says required ⊆ used, so fold any stragglers back in — otherwise
    // the extension never reaches the ratings list.
    const missingFromUsed = required.filter((name) => !used.includes(name));
    return { used: [...used, ...missingFromUsed], required };
  } catch {
    return { used: [], required: [] };
  }
}

/**
 * Parse a GLB with every extension the creator tools handle. `decoded` names
 * the compression the file used (Draco, meshopt); its geometry is now plain,
 * and the returned `io` writes it uncompressed.
 */
export async function readModel(buffer: ArrayBuffer): Promise<{ io: NodeIO; doc: Document; decoded: string[] }> {
  const io = new NodeIO().registerExtensions(EXTENSIONS);
  const used = readDeclaredExtensions(buffer).used;
  const compression = COMPRESSION.filter((c) => used.includes(c.name));
  for (const c of compression) {
    let decoder: unknown;
    try {
      decoder = await c.load();
    } catch (err) {
      throw new Error(`Couldn't load the ${c.label} decoder: ${err instanceof Error ? err.message : String(err)}`);
    }
    io.registerDependencies({ [c.key]: decoder });
  }
  const doc = await io.readBinary(new Uint8Array(buffer));
  for (const ext of doc.getRoot().listExtensionsUsed()) {
    if (compression.some((c) => c.name === ext.extensionName)) ext.dispose();
  }
  return { io, doc, decoded: compression.map((c) => c.label) };
}
